import { components } from "../types/api";

type Role = components["schemas"]["Role"];
type BriefingStatus = components["schemas"]["BriefingStatus"];
type BriefingType = components["schemas"]["_36_Enums.BriefingType"];

/**
 * Determines if a user role can edit a specific goal field
 */
export const canEditGoalField = (
  role: Role,
  field: string,
  briefingStatus?: BriefingStatus
): boolean => {
  const accountFields = [
    "startDate",
    "endDate",
    "currency",
    "objective",
    "country",
    "funnel",
    "campaignConcept",
    "campaign",
    "orderName",
    "lineName",
    "expectedVolume",
    "grossUnitCost",
    "totalInversion",
    "status",
    "notes",
  ];

  const pricingFields = [
    "margin",
    "netUnitCost",
    "platformInversion",
    "adtechCost",
    "zeotapDataCost",
    "adserverCost",
    "impressionTrackerCost",
    "clickTrackerCost",
  ];

  const technicianFields = [
    "visibility",
    "ctr",
    "netUnitCost",
    "zeotapDataCost",
    "adserverCost",
    "impressionTrackerCost",
    "clickTrackerCost",
    "adtechCost",
    "platformInversion",
    "status",
    "notes",
  ];

  switch (role) {
    case "ACCOUNT":
      return accountFields.includes(field);
    case "PRICING":
      return pricingFields.includes(field);
    case "TECHNICIAN":
      if (field === "margin" && briefingStatus === "READY_FOR_PRODUCTION") {
        return true;
      }
      return technicianFields.includes(field);
    case "ADMIN":
    case "STRATEGY":
      return true;
    default:
      return false;
  }
};

/**
 * Determines if a user role can view a specific goal field
 */
export const canViewGoalField = (role: Role, field: string): boolean => {
  // Account cannot see: margin, platformInversion, netUnitCost
  if (
    role === "ACCOUNT" &&
    ["margin", "platformInversion", "netUnitCost"].includes(field)
  ) {
    return false;
  }

  // Technician cannot see: totalInversion
  if (role === "TECHNICIAN") {
    if (["totalInversion"].includes(field)) {
      return false;
    }
  }

  // Client limited view
  if (role === "CLIENT") {
    const clientFields = [
      "startDate",
      "endDate",
      "currency",
      "objective",
      "country",
      "funnel",
      "campaignConcept",
      "campaign",
      "orderName",
      "lineName",
      "expectedVolume",
      "grossUnitCost",
      "totalInversion",
      "status",
      "notes",
    ];
    return clientFields.includes(field);
  }

  return true;
};

/**
 * Determines if a user role can edit the asset section
 */
export const canEditAssetSection = (
  role: Role,
  briefingStatus?: BriefingStatus
): boolean => {
  switch (role) {
    case "ADMIN":
    case "ACCOUNT":
    case "STRATEGY":
    case "PRICING":
      return true;
    case "TECHNICIAN":
      return briefingStatus === "READY_FOR_PRODUCTION";
    default:
      return false;
  }
};

/**
 * Determines if a user can transition a briefing from current status to new status
 */
export const canTransitionStatus = (
  role: Role,
  currentStatus: BriefingStatus,
  newStatus: BriefingStatus,
  briefingType?: BriefingType
): boolean => {
  // Admin and Strategy can do anything
  if (role === "ADMIN" || role === "STRATEGY" || role === "COMERCIAL") {
    return true;
  }

  const transitions: Record<
    Exclude<Role, "COMERCIAL">,
    Partial<Record<BriefingStatus, BriefingStatus[]>>
  > = {
    ACCOUNT: {
      DRAFT:
        briefingType === "PAID_MEDIA_MANAGED"
          ? ["READY_FOR_PRODUCTION", "ARCHIVED"]
          : ["READY_FOR_PRICING", "ARCHIVED"],
      READY_FOR_PRICING: ["DRAFT", "ARCHIVED"],
      READY_FOR_PRODUCTION:
        briefingType === "PAID_MEDIA_MANAGED"
          ? ["DRAFT", "ARCHIVED"]
          : ["ARCHIVED"],
      IN_PRODUCTION: ["ARCHIVED"],
      FINALIZED: ["ARCHIVED"],
    },
    PRICING: {
      READY_FOR_PRICING: ["READY_FOR_PRODUCTION", "DRAFT"],
      READY_FOR_PRODUCTION: ["READY_FOR_PRICING"],
    },
    TECHNICIAN: {
      READY_FOR_PRODUCTION: ["IN_PRODUCTION"],
      IN_PRODUCTION: ["READY_FOR_PRODUCTION"],
    },
    ADMIN: {}, // Handled above
    STRATEGY: {}, // Handled above
    CLIENT: {}, // No transitions allowed
  };

  const allowedTransitions = transitions[role]?.[currentStatus] || [];
  return allowedTransitions.includes(newStatus);
};

/**
 * Gets the next available status transitions for the current role and status
 */
export const getAvailableStatusTransitions = (
  role: Role,
  currentStatus: BriefingStatus,
  briefingType?: BriefingType
): BriefingStatus[] => {
  if (currentStatus === "FINALIZED") {
    return [];
  }

  if (role === "ADMIN" || role === "STRATEGY" || role === "COMERCIAL") {
    // Admin and Strategy can go to any status
    return [
      "DRAFT",
      "READY_FOR_PRICING",
      "READY_FOR_PRODUCTION",
      "IN_PRODUCTION",
      "FINALIZED",
      "ARCHIVED",
    ].filter((s) => s !== currentStatus) as BriefingStatus[];
  }

  const transitions: Record<
    Exclude<Role, "COMERCIAL">,
    Partial<Record<BriefingStatus, BriefingStatus[]>>
  > = {
    ACCOUNT: {
      DRAFT:
        briefingType === "PAID_MEDIA_MANAGED"
          ? ["READY_FOR_PRODUCTION", "ARCHIVED"]
          : ["READY_FOR_PRICING", "ARCHIVED"],
      READY_FOR_PRICING: ["DRAFT", "ARCHIVED"],
      READY_FOR_PRODUCTION:
        briefingType === "PAID_MEDIA_MANAGED"
          ? ["DRAFT", "ARCHIVED"]
          : ["ARCHIVED"],
      IN_PRODUCTION: ["ARCHIVED"],
      FINALIZED: ["ARCHIVED"],
    },
    PRICING: {
      READY_FOR_PRICING: ["READY_FOR_PRODUCTION", "DRAFT"],
      READY_FOR_PRODUCTION: ["READY_FOR_PRICING"],
    },
    TECHNICIAN: {
      READY_FOR_PRODUCTION: ["IN_PRODUCTION"],
      IN_PRODUCTION: ["READY_FOR_PRODUCTION"],
    },
    ADMIN: {},
    STRATEGY: {},
    CLIENT: {},
  };

  return transitions[role]?.[currentStatus] || [];
};

/**
 * Gets user-friendly status labels
 */
export const getStatusLabel = (status: BriefingStatus): string => {
  const labels: Record<BriefingStatus, string> = {
    DRAFT: "Draft",
    READY_FOR_PRICING: "Ready for Pricing",
    READY_FOR_PRODUCTION: "Ready for Production",
    IN_PRODUCTION: "In Production",
    FINALIZED: "Finalized",
    ARCHIVED: "Archived",
  };
  return labels[status] || status;
};

/**
 * Gets button label for transitioning to a status
 */
export const getTransitionButtonLabel = (
  fromStatus: BriefingStatus,
  toStatus: BriefingStatus
): string => {
  const labels: Record<string, string> = {
    "DRAFT->READY_FOR_PRICING": "Mark Ready for Pricing",
    "DRAFT->READY_FOR_PRODUCTION": "Mark Ready for Production",
    "READY_FOR_PRICING->DRAFT": "Send Back to Draft",
    "READY_FOR_PRICING->READY_FOR_PRODUCTION": "Mark Ready for Production",
    "READY_FOR_PRODUCTION->READY_FOR_PRICING": "Send Back to Pricing",
    "READY_FOR_PRODUCTION->DRAFT": "Send Back to Draft",
    "READY_FOR_PRODUCTION->IN_PRODUCTION": "Mark In Production",
    "IN_PRODUCTION->READY_FOR_PRODUCTION": "Move Back to Production Setup",
  };

  const key = `${fromStatus}->${toStatus}`;
  return labels[key] || `Change to ${getStatusLabel(toStatus)}`;
};
